import { useEffect, useMemo, useRef } from "react";
import { Box } from "@mui/material";
import { useCalendarDateSelectorState, useSetCalendarDateSelector } from "./states/useCalendarDateSelector";
import { CalendarMode, DateRange } from "./types";
import { CalendarModeSelector } from "./components/CalendarModeSelector";
import { CalendarContent } from "./components/CalendarContent";
import { CalendarClearButton } from "./components/CalendarClearButton";
import { CalendarSelectionSummary } from "./components/CalendarSelectionSummary";

const MODE_LABELS: Record<CalendarMode, string> = {
  day: "Day",
  week: "Week",
  month: "Month",
  quarter: "Quarter",
  year: "Year",
};

interface CalendarDateSelectorProps {
  onFilter: (range: DateRange) => void;
  width?: number | string;
  height?: string;
  modes?: CalendarMode[];
  defaultMode?: CalendarMode;
  allowFutureDates?: boolean;
  showRangeOption?: boolean;
  hideRangeOnModes?: CalendarMode[];
  autoSelectCurrent?: boolean;
  hideCalendarContent?: boolean;
  showSelectionSummary?: boolean;
}

export default function CalendarDateSelector({
  onFilter,
  width = 300,
  height = "fit-content",
  modes = ["day", "week", "month"],
  defaultMode,
  allowFutureDates = false,
  showRangeOption = true,
  hideRangeOnModes = [],
  autoSelectCurrent = false,
  hideCalendarContent = false,
  showSelectionSummary = false,
}: CalendarDateSelectorProps) {
  const { range } = useCalendarDateSelectorState();

  const didApplyDefaultMode = useRef(false);
  const { setField, setPayload } = useSetCalendarDateSelector();
  useEffect(() => {
    if (didApplyDefaultMode.current || !defaultMode) return;

    if (autoSelectCurrent) {
      const currentRange = getCurrentRangeByMode(defaultMode);

      setPayload({
        mainMode: defaultMode,
        viewDate: new Date(),
        range: currentRange,
        isRangePicker: false,
        showCard: true,
      });

      if (currentRange.start && currentRange.end) {
        onFilter({
          start: currentRange.start.toISOString(),
          end: currentRange.end.toISOString(),
        });
      }
    } else {
      setField("mainMode", defaultMode);
    }

    didApplyDefaultMode.current = true;
  }, [autoSelectCurrent, defaultMode, onFilter, setField, setPayload]);

  const getCurrentRangeByMode = (mode: CalendarMode) => {
    const today = new Date();

    if (mode === "day") {
      const start = new Date(today);
      start.setHours(0, 0, 0, 0);

      const end = new Date(today);
      end.setHours(23, 59, 59, 999);

      return { start, end };
    }

    if (mode === "week") {
      const start = new Date(today);
      start.setDate(today.getDate() - today.getDay());
      start.setHours(0, 0, 0, 0);

      const end = new Date(start);
      end.setDate(start.getDate() + 6);
      end.setHours(23, 59, 59, 999);

      return { start, end };
    }

    if (mode === "month") {
      const start = new Date(today.getFullYear(), today.getMonth(), 1);
      start.setHours(0, 0, 0, 0);

      const end = new Date(today.getFullYear(), today.getMonth() + 1, 0);
      end.setHours(23, 59, 59, 999);

      return { start, end };
    }

    return { start: null, end: null };
  };

  const normalizeRangeToDayBounds = (start: Date | null, end: Date | null): DateRange => {
    if (!start || !end) {
      return {
        start: start ? start.toISOString() : null,
        end: end ? end.toISOString() : null,
      };
    }

    const normalizedStart = new Date(start);
    normalizedStart.setHours(0, 0, 0, 0);

    const normalizedEnd = new Date(end);
    normalizedEnd.setHours(23, 59, 59, 999);

    return {
      start: normalizedStart.toISOString(),
      end: normalizedEnd.toISOString(),
    };
  };

  const filteredModeLabels = useMemo(() => {
    const filtered: Partial<Record<CalendarMode, string>> = {};

    modes.forEach((mode) => {
      filtered[mode] = MODE_LABELS[mode];
    });

    return filtered as Record<CalendarMode, string>;
  }, [modes]);

  const handleConfirm = () => {
    const isoRange = normalizeRangeToDayBounds(range.start, range.end);
    onFilter(isoRange);
  };

  const handleClear = () => {
    onFilter({ start: null, end: null });
  };

  return (
    <Box display="flex" flexDirection="column" gap={1} justifyContent="center" alignItems="center" sx={{ width }}>
      <CalendarModeSelector modes={filteredModeLabels} />

      {!hideCalendarContent && (
        <>
          <CalendarContent
            allowFutureDates={allowFutureDates}
            width={width}
            height={height}
            onConfirm={handleConfirm}
            showRangeOption={showRangeOption}
            hideRangeOnModes={hideRangeOnModes}
            autoSelectCurrent={autoSelectCurrent}
          />

          {showSelectionSummary && (
            <Box sx={{ width: "100%", bgcolor: "var(--fx-surface-base, #FFFFFF)", borderRadius: 3, px: 2, py: 1.5 }}>
              <CalendarSelectionSummary />
            </Box>
          )}

          <CalendarClearButton onClear={handleClear} />
        </>
      )}
    </Box>
  );
}
